import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { mapParamsBySchema } from '../util';
import { apiRequest } from '../Services/Common/ApiRequest';
import Global from '../Services/Common/Global';
import { M_ALL_BASIC_Schema } from '../Scripts/AlarmList/M_ALL_BASIC_Schema';

/**
 * 알림 목록 API 응답에서 row 배열을 추출한다.
 * @param {Object} response - GetAlarmList API 응답
 * @returns {Array} 알림 목록 배열
 */
const getAlarmListRows = (response) => {
  const rows = response?.data;
  return Array.isArray(rows) ? rows : [];
};

/**
 * 알림 목록 React Query 캐시 키를 반환한다.
 * useSearch1(알림 화면 조회)과 동일한 키를 사용해 캐시를 공유한다.
 * @returns {Array} React Query queryKey
 */
export const getAlarmListQueryKey = () => {
  const params = mapParamsBySchema(M_ALL_BASIC_Schema.mapper.search1);
  return ['Alarm', 'GetAlarmList', params];
};

/**
 * 알림을 읽음 처리했을 때 React Query 캐시의 useYn을 'Y'로 갱신한다.
 * AppBar 안읽음 배지가 즉시 반영되도록 한다.
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {string} alarmCd - 읽음 처리할 알림 코드
 */
export const markAlarmAsReadInCache = (queryClient, alarmCd) => {
  queryClient.setQueryData(getAlarmListQueryKey(), (old) => {
    const rows = getAlarmListRows(old);
    if (!rows.length) return old;

    return {
      ...old,
      data: rows.map((item) =>
        item.alarmCd === alarmCd ? { ...item, useYn: 'Y' } : item
      ),
    };
  });
};

/**
 * 알림 삭제 후 React Query 캐시에서 해당 항목을 제거한다.
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {string} alarmCd - 삭제된 알림 코드
 */
export const removeAlarmFromCache = (queryClient, alarmCd) => {
  queryClient.setQueryData(getAlarmListQueryKey(), (old) => {
    const rows = getAlarmListRows(old);
    if (!rows.length) return old;

    return {
      ...old,
      data: rows.filter((item) => item.alarmCd !== alarmCd),
    };
  });
};

/**
 * 로그인 사용자의 안읽은 알림(useYn === 'N') 존재 여부를 조회한다.
 * AppBar 알림 아이콘 배지·깜박임 표시에 사용한다.
 * @returns {{ hasUnread: boolean, refetchUnread: Function }}
 *   - hasUnread: 안읽은 알림이 있으면 true
 *   - refetchUnread: 알림 목록 재조회 (FCM 수신 등)
 */
export const useHasUnreadAlarm = () => {
  const { userInfo } = useSelector((state) => state.BaseInfo);

  const query = useQuery({
    queryKey: getAlarmListQueryKey(),
    queryFn: () =>
      apiRequest(
        'post',
        Global.urls.Alarm.GetAlarmList,
        mapParamsBySchema(M_ALL_BASIC_Schema.mapper.search1)
      ),
    enabled: !!userInfo?.userid,
    select: (response) =>
      getAlarmListRows(response).some((item) => item.useYn === 'N'),
  });

  return {
    hasUnread: !!query.data,
    refetchUnread: query.refetch,
  };
};
